import React, { ReactNode, useCallback, useEffect } from "react";
import { useLoginComponents } from "./react.login";
import { useDebug } from "@me8eon/debug";
import { authenticateDebugName, useAccessTokenFn, useLogin, useUserData } from "./authentication.provider";
import { LoginOutFn } from "@me8eon/authentication";
import { delay } from "@me8eon/time";
import { LoadingOr } from "@me8eon/async";
import { useFeatureFlag } from "@me8eon/featureflags";


export type RefreshLoginStrategy = (refreshLogin: LoginOutFn) => LoginOutFn;

export type AuthenticateProps = {
    children: React.ReactNode
    LoadingDisplay?: () => ReactNode
    _refreshLogin?: (refreshLogin: LoginOutFn) => LoginOutFn

};

export const delayedRefreshLogin: RefreshLoginStrategy = (refreshLogin: LoginOutFn): LoginOutFn =>
    async (debugLog) => {
        await delay(1000);
        return await refreshLogin(debugLog);
    };

export const noDelayRefreshLogin: RefreshLoginStrategy = (refreshLogin: LoginOutFn): LoginOutFn =>
    async (debugLog) => refreshLogin(debugLog);

/** When we load the page we need to refresh any logged in tokens for authenticating.
 * If we set notLoggedIn then we will show the notLoggedIn component until we have refreshed the login.
 * */
export function Authenticate({ children = null, LoadingDisplay, _refreshLogin = noDelayRefreshLogin }: AuthenticateProps) {
    const { refreshLogin } = useLogin();
    const { NotLoggedIn } = useLoginComponents();
    const kleisli: LoginOutFn = useCallback(_refreshLogin(refreshLogin), [refreshLogin, _refreshLogin]);
    const debug = useDebug(authenticateDebugName);
    return <LoadingOr kleisli={kleisli} input={debug} loading={LoadingDisplay}>{() =>
        NotLoggedIn ? <MustBeLoggedIn renderNotLoggedIn={NotLoggedIn}>{children}</MustBeLoggedIn> : children
    }</LoadingOr>;
}

export type MustBeLoggedInProps = {
    renderNotLoggedIn: () => ReactNode,
    children: ReactNode
};

export type MustBeLoggedInDisplay = (props: MustBeLoggedInProps) => React.ReactElement;

export function MustBeLoggedIn({ children, renderNotLoggedIn }: MustBeLoggedInProps): ReactNode {
    const userData = useUserData();
    const debug = useDebug(authenticateDebugName);
    debug("MustBeLoggedIn", userData);
    return (
        <MustBeLoggedInWrapper
            loggedIn={userData?.loggedIn}
            children={children}
            notLoggedIn={renderNotLoggedIn}
        />
    );
}

// The wrapper that decides which content to show
function MustBeLoggedInWrapper({
                                   loggedIn,
                                   children,
                                   notLoggedIn,
                               }: {
    loggedIn: boolean;
    children: ReactNode;
    notLoggedIn: () => ReactNode;
}) {
    // We do not conditionally skip any hooks here.
    // If we had more hooks, they'd always be called.

    // We only conditionally show different content.
    if (loggedIn) {
        // If children is a component that uses hooks, that's fine,
        // we always render children in the "loggedIn" path consistently
        return <>{children}</>;
    } else {
        // If notLoggedIn uses hooks, it is always used in the "loggedOut" path consistently
        return <>{notLoggedIn()}</>;
    }
}

/* Because the ai chat bot doesn't use hooks, we can't feed it the access token without major code changes
* We expect to be doing those code changes in a couple of sprints time
* Until then this keeps it working by keeping the local storage token up to date (it polls every five minutes)
*
* */
export function AccessTokenScaffoldingProvider({ children }: { children: ReactNode }) {
    const userData = useUserData();
    const dlsAccessTokenFn = useAccessTokenFn("dls");
    useEffect(() => {
        dlsAccessTokenFn(); // don't need to await for this. We want to call this at the start of the period as well as the end
        const pid = setInterval(dlsAccessTokenFn, 300000);
        return () => clearInterval(pid);
    }, [userData]);//why userdata? Because it changes when we log in and out
    return children;
}
